import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaFileExcel, FaSyncAlt, FaUsers } from 'react-icons/fa';
import AdminNav from '../components/admin/AdminNav';
import Button from '../components/common/Button';
import Heading from '../components/common/Heading';
import Section from '../components/common/Section';
import COURSES_DATA from '../data/coursesdata';
import { fetchRegistrations, syncRegistrationPayment } from '../services/registrationService';
import { clearAdminToken, getAdminToken } from '../utils/adminAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

const formatDate = (value) => {
  if (!value) {
    return 'N/A';
  }

  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(new Date(value));
};

const escapeCsvValue = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  const stringValue = String(value);

  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const AdminRegistrationsPage = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [syncingPaymentId, setSyncingPaymentId] = useState('');

  const courseNameById = useMemo(() => {
    const courses = Object.values(COURSES_DATA).flatMap((category) => category.courses);
    return courses.reduce((map, course) => {
      map[course.id] = course.title;
      return map;
    }, {});
  }, []);

  const loadRegistrations = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await fetchRegistrations(getAdminToken());
      setRegistrations(data);
      setHasLoaded(true);
    } catch (requestError) {
      if (requestError.response?.status === 401) {
        clearAdminToken();
      }
      setError(requestError.response?.data?.message || 'Unable to load registrations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPhotoUrl = (path) => {
    if (!path) {
      return null;
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    return `${API_ORIGIN}${path}`;
  };

  const renderCourseNames = (selectedCourses) => {
    if (!Array.isArray(selectedCourses) || selectedCourses.length === 0) {
      return 'N/A';
    }

    return selectedCourses.map((courseId) => courseNameById[courseId] || courseId).join(', ');
  };

  const handleSyncPayment = async (paymentId) => {
    setSyncingPaymentId(paymentId);
    setError('');

    try {
      await syncRegistrationPayment(paymentId, getAdminToken());
      await loadRegistrations();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to sync payment status.');
    } finally {
      setSyncingPaymentId('');
    }
  };

  const handleExportRegistrations = () => {
    const headers = [
      'Registration ID',
      'Full Name',
      'Date of Birth',
      'Gender',
      'Other Gender',
      'Email',
      'WhatsApp Number',
      'Permanent Address',
      'Freetown Area',
      'Education Level',
      'Career Path',
      'Institution',
      'Computer Skill',
      'Selected Courses',
      'Heard From',
      'Referrer Name',
      'Expectations',
      'Payment Status',
      'Payment Reference',
      'Payment Amount',
      'Payment USSD Code',
      'Passport Photo URL',
      'Submitted Date'
    ];

    const rows = registrations.map((registration) => [
      registration.registration_number,
      registration.full_name,
      formatDate(registration.date_of_birth),
      registration.gender,
      registration.other_gender,
      registration.email,
      registration.whatsapp_number,
      registration.permanent_address,
      registration.freetown_area,
      registration.education_level,
      registration.career_path,
      registration.institution,
      registration.computer_skill,
      renderCourseNames(registration.selected_courses),
      registration.heard_from,
      registration.referrer_name,
      registration.expectations,
      registration.payment_status,
      registration.payment_reference,
      registration.payment_amount ? `NLe ${Number(registration.payment_amount).toLocaleString('en')}` : '',
      registration.payment_ussd_code,
      getPhotoUrl(registration.passport_photo_path),
      formatDate(registration.created_at)
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(','))
      .join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = `afrovate-registrations-${dateStamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <Helmet>
        <title>Admin Registrations - Afrovate Tech Hub</title>
      </Helmet>

      <section className="bg-primary-dark pt-32 pb-16 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <FaUsers className="text-secondary text-3xl" />
              <h1 className="text-4xl md:text-5xl font-bold">Registration Admin</h1>
            </div>
            <p className="text-white text-opacity-90 text-lg">
              View applicants who submitted the Afrovate Tech Hub training registration form.
            </p>
          </div>
        </div>
      </section>

      <Section background="white" padding="lg">
        <AdminNav />

        <div className="flex justify-end mb-6">
          <Button variant="outline-primary" onClick={loadRegistrations} disabled={loading} icon={<FaSyncAlt className={loading ? 'animate-spin' : ''} />}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
        {error && <p className="text-red-600 mb-6">{error}</p>}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <Heading level={2}>Applicants</Heading>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-gray-600">{registrations.length} registration{registrations.length !== 1 ? 's' : ''}</p>
            <Button
              variant="outline-primary"
              onClick={handleExportRegistrations}
              disabled={registrations.length === 0}
              icon={<FaFileExcel />}
            >
              Export to Excel
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Photo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Applicant</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Courses</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Background</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Submitted</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {registrations.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-gray-500">
                    {hasLoaded ? 'No registrations found.' : 'Enter the admin token and load registrations.'}
                  </td>
                </tr>
              ) : (
                registrations.map((registration) => {
                  const photoUrl = getPhotoUrl(registration.passport_photo_path);

                  return (
                    <tr key={registration.id} className="align-top">
                      <td className="px-4 py-4">
                        {photoUrl ? (
                          <a href={photoUrl} target="_blank" rel="noopener noreferrer">
                            <img
                              src={photoUrl}
                              alt={registration.full_name}
                              className="h-14 w-14 rounded-full object-cover border border-gray-200"
                            />
                          </a>
                        ) : (
                          <div className="h-14 w-14 rounded-full bg-gray-100"></div>
                        )}
                      </td>
                      <td className="px-4 py-4 min-w-[220px]">
                        <p className="font-semibold text-primary-dark">{registration.full_name}</p>
                        <p className="text-sm font-semibold text-secondary">{registration.registration_number}</p>
                        <p className="text-sm text-gray-600">{registration.gender}{registration.other_gender ? `: ${registration.other_gender}` : ''}</p>
                        <p className="text-sm text-gray-600">DOB: {formatDate(registration.date_of_birth)}</p>
                        <p className="text-sm text-gray-600">{registration.freetown_area}</p>
                      </td>
                      <td className="px-4 py-4 min-w-[220px]">
                        <p className="text-sm text-gray-700">{registration.email}</p>
                        <p className="text-sm text-gray-700">{registration.whatsapp_number}</p>
                        <p className="text-sm text-gray-600">{registration.permanent_address}</p>
                      </td>
                      <td className="px-4 py-4 min-w-[260px] text-sm text-gray-700">
                        {renderCourseNames(registration.selected_courses)}
                      </td>
                      <td className="px-4 py-4 min-w-[220px]">
                        {registration.payment_reference ? (
                          <>
                            <p className="text-sm font-semibold text-primary-dark">{registration.payment_status || 'pending'}</p>
                            <p className="text-sm text-gray-600">Ref: {registration.payment_reference}</p>
                            <p className="text-sm text-gray-600">Amount: NLe {Number(registration.payment_amount).toLocaleString('en')}</p>
                            {registration.payment_ussd_code && (
                              <p className="text-sm text-gray-600 break-all">USSD: {registration.payment_ussd_code}</p>
                            )}
                            {registration.payment_id && (
                              <button
                                type="button"
                                onClick={() => handleSyncPayment(registration.payment_id)}
                                disabled={syncingPaymentId === registration.payment_id}
                                className="mt-2 text-sm font-semibold text-primary hover:text-primary-dark disabled:opacity-60"
                              >
                                {syncingPaymentId === registration.payment_id ? 'Checking...' : 'Check Status'}
                              </button>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-gray-500">No payment code</p>
                        )}
                      </td>
                      <td className="px-4 py-4 min-w-[260px]">
                        <p className="text-sm text-gray-700">{registration.education_level}</p>
                        <p className="text-sm text-gray-600">{registration.career_path}</p>
                        <p className="text-sm text-gray-600">{registration.institution}</p>
                        <p className="text-sm text-gray-600">Computer skill: {registration.computer_skill}</p>
                        <p className="text-sm text-gray-600">Heard from: {registration.heard_from}</p>
                        {registration.referrer_name && (
                          <p className="text-sm text-gray-600">Referrer: {registration.referrer_name}</p>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(registration.created_at)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
};

export default AdminRegistrationsPage;
